import type { AgentModel } from "../api/e2e";

export interface AgentModelLineageEntry {
  depth: number;
  directIterations: number;
}

export interface AgentModelLineageTreeNode {
  id: string;
  children: readonly AgentModelLineageTreeNode[];
}

type LineageModel = Pick<AgentModel, "base_model" | "id">;

export function buildAgentModelLineage(
  models: readonly LineageModel[],
): ReadonlyMap<string, AgentModelLineageEntry> {
  const modelsById = new Map(models.map((model) => [model.id, model]));
  const directIterations = new Map<string, number>();
  for (const model of models) {
    const parentId = model.base_model?.model_version_id;
    if (!parentId) continue;
    directIterations.set(parentId, (directIterations.get(parentId) ?? 0) + 1);
  }

  const depths = new Map<string, number>();
  const depthFor = (
    model: LineageModel,
    ancestors: ReadonlySet<string>,
  ): number => {
    const cached = depths.get(model.id);
    if (cached !== undefined) return cached;
    const parentId = model.base_model?.model_version_id;
    if (!parentId) {
      depths.set(model.id, 0);
      return 0;
    }
    if (ancestors.has(parentId)) return 0;
    const parent = modelsById.get(parentId);
    if (!parent) {
      depths.set(model.id, 1);
      return 1;
    }
    const nextAncestors = new Set(ancestors);
    nextAncestors.add(model.id);
    const depth = depthFor(parent, nextAncestors) + 1;
    depths.set(model.id, depth);
    return depth;
  };

  return new Map(
    models.map((model) => [
      model.id,
      {
        depth: depthFor(model, new Set()),
        directIterations: directIterations.get(model.id) ?? 0,
      },
    ]),
  );
}

export function buildAgentModelLineageTree(
  models: readonly LineageModel[],
): readonly AgentModelLineageTreeNode[] {
  const modelsById = new Map(models.map((model) => [model.id, model]));
  const childrenByParent = new Map<string, LineageModel[]>();
  const roots: LineageModel[] = [];

  for (const model of models) {
    const parentId = model.base_model?.model_version_id;
    if (!parentId || parentId === model.id || !modelsById.has(parentId)) {
      roots.push(model);
      continue;
    }
    const children = childrenByParent.get(parentId) ?? [];
    children.push(model);
    childrenByParent.set(parentId, children);
  }

  const rendered = new Set<string>();
  const buildNode = (
    model: LineageModel,
    ancestors: ReadonlySet<string>,
  ): AgentModelLineageTreeNode | null => {
    if (rendered.has(model.id) || ancestors.has(model.id)) return null;
    rendered.add(model.id);
    const nextAncestors = new Set(ancestors);
    nextAncestors.add(model.id);
    const children = (childrenByParent.get(model.id) ?? [])
      .map((child) => buildNode(child, nextAncestors))
      .filter((child): child is AgentModelLineageTreeNode => child !== null);
    return { id: model.id, children };
  };

  const tree = roots
    .map((root) => buildNode(root, new Set()))
    .filter((node): node is AgentModelLineageTreeNode => node !== null);

  for (const model of models) {
    const node = buildNode(model, new Set());
    if (node) tree.push(node);
  }
  return tree;
}

export function pendingModelLineageId(runId: string): string {
  return `pending:${runId}`;
}
